import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { data } from '@portal/pkg-api/shared'
import { isAxiosError } from 'axios'
import { NavbarApi as NavbarApiInternal } from '@portal/pkg-api/gen/admin-console'
import { AdminConsoleApiConfiguration } from '@portal/pkg-api/config'

export const NavbarApi = new NavbarApiInternal(AdminConsoleApiConfiguration)

/** Absolute URL used for authenticated <img> preview (session cookies). */
export const NAVBAR_LOGO_URL = '/api/admin/navbar/logo'

const prefix = 'admin.preferences.navbarLogo'

export type NavbarLogoState = {
  hasLogo: boolean
  /** Cache-buster for img src after upload/replace. */
  version: number
}

export const NavbarLogoQueries = {
  state: () => ({
    queryKey: [`${prefix}.state`] as const,
    queryFn: async (): Promise<NavbarLogoState> => {
      try {
        await NavbarApi.getNavbarLogo({
          mercatusNotifyOnError: false,
          ignore404: true,
          responseType: 'blob',
        })
        return { hasLogo: true, version: Date.now() }
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return { hasLogo: false, version: Date.now() }
        }
        throw error
      }
    },
    retry: (failureCount: number, error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false
      }
      return failureCount < 2
    },
  }),
}

export function useNavbarLogoState() {
  return useQuery(NavbarLogoQueries.state())
}

function invalidateLogoQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: NavbarLogoQueries.state().queryKey })
  void queryClient.invalidateQueries({ queryKey: ['shared.config.configBase'] })
}

export function useUploadNavbarLogo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File) =>
      data(NavbarApi.putNavbarLogo(file, { mercatusNotifyOnError: false })),
    onSuccess: () => {
      invalidateLogoQueries(queryClient)
    },
  })
}

export function useDeleteNavbarLogo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => data(NavbarApi.deleteNavbarLogo({ mercatusNotifyOnError: false })),
    onSuccess: () => {
      invalidateLogoQueries(queryClient)
    },
  })
}
